/**
 * MissionsAsEngagementStrip — adapts brand-challenge data to the slot
 * system (DropEngagementStrip + MechanicSlot + mechanic library).
 *
 * Replaces MissionsSection in the Drop page's MISSIONS section dispatch.
 * Each active questConfig becomes a sponsor engagement; the first one fills
 * the marquee, the rest become secondary tiles. Each quest's milestones are
 * rendered as MechanicJourney steps — done/active/empty derived from the
 * user's progression data.
 *
 * Falls back to a slim empty state when the challenge has no active
 * questConfigs (uncommon — usually means the challenge is pre-launch or
 * paused). The fallback preserves brand chrome so the section still has
 * visual presence.
 */

import React from "react";
import {useGetChallenge} from "@/hooks/challenges/useGetChallenge";
import {DropEngagementStrip, type DropStripMarquee, type DropStripTile} from "./mechanics/DropEngagementStrip";
import {MechanicJourney, type JourneyStep} from "./mechanics/MechanicJourney";
import type {MechanicBrandChrome} from "./mechanics/MechanicSlot";

/*
 * Local narrowing types — the generated `ChallengeInfoResponse` carries
 * a broader shape than this adapter actually consumes. Defining these
 * structural interfaces gives the data path real type safety without
 * over-coupling to the full client surface. If the backend renames
 * `questConfigs` or removes `milestones`, tsc surfaces the regression
 * here instead of letting it degrade silently to an empty strip.
 */
interface QuestMilestone {
    id: string;
    name?: string;
    progressionOrder?: number;
}

interface RewardConfig {
    rewardType?: string;
    rewardName?: string;
}

interface QuestConfig {
    id: string;
    name: string;
    description?: string;
    isActive?: boolean;
    milestones?: QuestMilestone[];
    rewardConfigs?: RewardConfig[];
}

interface ProgressionEntry {
    questConfigId?: string;
    progression?: Record<string, { isComplete?: boolean; amount?: number }>;
}

interface ChallengeWithQuests {
    brandName?: string;
    questConfigs?: QuestConfig[];
    progressions?: ProgressionEntry[];
}

interface MissionsAsEngagementStripProps {
    challengeId?: string;
    /** Brand name fallback when the challenge response is missing one. */
    hubBrandName?: string;
}

/*
 * Sponsor → slot brandKey. Anything we don't recognize falls through to
 * "generic" so the slot still renders with neutral chrome. Extend the SCSS
 * brand color map in MechanicSlot.scss to add new sponsors.
 */
function resolveBrandKey(brandName?: string): string {
    const k = (brandName ?? "").trim().toLowerCase();
    if (!k) return "generic";
    if (k.includes("chipotle")) return "chipotle";
    if (k.includes("scuf")) return "scuf";
    if (k.includes("g fuel") || k.includes("gfuel")) return "gfuel";
    if (k.includes("monster")) return "monster";
    if (k.includes("red bull") || k.includes("redbull")) return "redbull";
    if (k.includes("m&m") || k.includes("mms")) return "mms";
    return "generic";
}

/*
 * Each milestone → one journey step. Done state derives from the user's
 * progression entry: completed milestones come back as isComplete=true,
 * the next-in-order milestone is active, the rest are empty.
 */
function buildJourneySteps(
    questConfig: QuestConfig,
    progressionMap: Record<string, { isComplete?: boolean; amount?: number }> | undefined,
): JourneyStep[] {
    const milestones = questConfig.milestones ?? [];
    /*
     * Sort by progressionOrder so the journey reads left-to-right in the
     * authored sequence. Missing order → end of list.
     */
    const ordered = [...milestones].sort((a, b) =>
        (a.progressionOrder ?? 999) - (b.progressionOrder ?? 999));

    let activeAssigned = false;
    return ordered.map(m => {
        const entry = progressionMap?.[m.id];
        const done = !!entry?.isComplete;
        const active = !done && !activeAssigned;
        if (active) activeAssigned = true;
        return {
            id: m.id,
            label: m.name ?? `Step ${m.progressionOrder ?? "?"}`,
            done,
            active,
        };
    });
}

/*
 * Reward summary for the slot's reward line. We don't render the reward
 * details (the reward catalog is its own surface) — just a short label that
 * primes the user on what they're working toward.
 */
function rewardLabel(questConfig: QuestConfig): string | undefined {
    const first = questConfig.rewardConfigs?.[0];
    if (!first) return undefined;
    return first.rewardName || first.rewardType;
}

export const MissionsAsEngagementStrip: React.FC<MissionsAsEngagementStripProps> = ({
    challengeId, hubBrandName,
}) => {
    const {data: challenge, isLoading} = useGetChallenge(challengeId);

    if (isLoading) {
        return <div className="missions-strip missions-strip--loading" aria-busy="true" />;
    }
    if (!challenge) {
        return null;
    }

    /*
     * Narrow once at the boundary — `useGetChallenge` returns the broader
     * `ChallengeInfoResponse` shape, but we only consume the quest-related
     * fields. Casting through unknown documents the boundary and gives
     * downstream code real type safety.
     */
    const cast = challenge as unknown as ChallengeWithQuests;
    const brandName = cast.brandName || hubBrandName;
    const brandKey = resolveBrandKey(brandName);
    const activeQuests = (cast.questConfigs ?? []).filter(q => q.isActive);

    if (activeQuests.length === 0) {
        return null;
    }

    /*
     * Build the progression lookup once — each quest reads its milestone
     * states from this map. Missing entries → user hasn't started.
     */
    const progressionByQuestConfigId: Record<string, Record<string, { isComplete?: boolean; amount?: number }>> = {};
    for (const p of cast.progressions ?? []) {
        if (p.questConfigId) progressionByQuestConfigId[p.questConfigId] = p.progression ?? {};
    }

    const [first, ...rest] = activeQuests;

    const marqueeChrome: MechanicBrandChrome = {
        brandKey,
        brandName: brandName ?? "Sponsored",
        brandMark: (brandName ?? "?").charAt(0).toUpperCase(),
        title: first.name,
        status: first.description ?? undefined,
        isLive: true,
        reward: rewardLabel(first),
        ctaLabel: "View Mission",
        ctaHref: "#",
    };

    const marquee: DropStripMarquee = {
        chrome: marqueeChrome,
        body:
            <MechanicJourney
                variant="marquee"
                steps={buildJourneySteps(first, progressionByQuestConfigId[first.id])}
            />,
    };

    const secondaries: DropStripTile[] = rest.map((q) => {
        const steps = buildJourneySteps(q, progressionByQuestConfigId[q.id]);
        const doneCount = steps.filter(s => s.done).length;
        return {
            chrome: {
                brandKey,
                brandName: brandName ?? "Sponsored",
                brandMark: (brandName ?? "?").charAt(0).toUpperCase(),
                title: q.name,
                ctaHref: "#",
            },
            body: <MechanicJourney variant="tile" steps={steps} />,
            typeLabel: `${doneCount} / ${steps.length}`,
        };
    });

    return (
        <DropEngagementStrip
            layout="stream-below"
            marquee={marquee}
            secondaries={secondaries}
        />
    );
};
